/* eslint-disable */
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import moment from 'moment';
import { valorPorExtenso } from '../../../shared/utils/valorPorExtenso';

export interface IComprovanteBaixaConta {
  numero: string;
  parcela?: string;
  valor: number;
  // O que é esse lançamento (campo "Histórico" do título em Contas a
  // Receber, ex.: "Mensalidade do sistema referente a setembro/2026") —
  // mostrado como referência do que foi pago, não é o histórico da forma
  // de pagamento (isso é outra coisa, ver IComprovanteBaixaFormaPagamento).
  historico?: string;
}

export interface IComprovanteBaixaFormaPagamento {
  nome: string;
  valor: number;
}

export interface IComprovanteBaixaDados {
  parceiro: string;
  contaNome: string;
  datalan: Date;
  contas: IComprovanteBaixaConta[];
  formasPagamento: IComprovanteBaixaFormaPagamento[];
  totalBaixado: number;
  desconto: number;
  acrescimo: number;
  logo?: string;
  // Nome de quem emite o comprovante (a empresa, não o parceiro) — mesmo
  // padrão do carnê de venda (impressaocarne.ts).
  empresaFantasia?: string;
  empresaRazaoSocial?: string;
  // Texto livre digitado na hora de imprimir (o que foi combinado com o
  // cliente) — ver DialogVisualizarComprovante.tsx. Não é gravado no banco.
  combinado?: string;
  // 'pagamento' = conta a pagar já paga (ver ListagemContasPagar) — muda só
  // os textos (comprovante de pagamento, valor pago, assinatura do recebedor).
  tipo?: 'recebimento' | 'pagamento';
  // CPF/CNPJ do parceiro — no recibo de pagamento, vai embaixo da assinatura.
  documentoParceiro?: string;
}

const formatarDocumento = (doc?: string) => {
  const n = (doc || '').replace(/\D/g, '');
  if (n.length === 11) return n.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  if (n.length === 14) return n.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  return doc || '';
};

// Recibo de pagamento (Contas a Pagar): declaração que o PARCEIRO assina
// confirmando que recebeu o valor — "Recebi de <empresa> a importância de
// R$ X (por extenso), referente a ..., pago via ...".
export const declaracaoRecebimento = (dados: IComprovanteBaixaDados) => {
  const empresa = dados.empresaFantasia || dados.empresaRazaoSocial || 'a empresa';
  const referencia = dados.contas.map((c) => c.historico || (c.numero ? `conta nº ${c.numero}${c.parcela ? ` (parcela ${c.parcela})` : ''}` : '')).filter(Boolean).join('; ');
  const formas = dados.formasPagamento.map((f) => f.nome).filter(Boolean).join(', ');
  const valor = Number(dados.totalBaixado || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  return `Recebi de ${empresa} a importância de ${valor} (${valorPorExtenso(dados.totalBaixado)})`
    + `${referencia ? `, referente a ${referencia}` : ''}`
    + `${formas ? `, paga via ${formas}` : ''}`
    + ', dando plena quitação deste valor.';
};

export const linhaAssinaturaRecebedor = (dados: IComprovanteBaixaDados) => {
  const doc = formatarDocumento(dados.documentoParceiro);
  return `${dados.parceiro}${doc ? ` — CPF/CNPJ ${doc}` : ''}`;
};

// Textos que mudam entre o comprovante de recebimento (Contas a Receber) e o
// de pagamento (Contas a Pagar) — usados aqui e em ComprovanteBaixaTermica.ts.
export const textosComprovante = (dados: IComprovanteBaixaDados) => (dados.tipo === 'pagamento'
  ? { titulo: 'RECIBO DE PAGAMENTO', parceiro: 'Favorecido', valor: 'Valor pago', total: 'TOTAL PAGO', contas: 'CONTAS PAGAS', assinaturaParceiro: 'Assinatura do Recebedor' }
  : { titulo: 'COMPROVANTE DE RECEBIMENTO', parceiro: 'Cliente', valor: 'Valor recebido', total: 'TOTAL RECEBIDO', contas: 'CONTAS BAIXADAS', assinaturaParceiro: 'Assinatura do Cliente' });

// Mesmo carregamento de logo (com fallback gerado em canvas) de
// ImpressaoReceber.ts — repetido aqui em vez de importado porque aquele
// arquivo já chama `pdfMake.createPdf(...).open()` direto, e aqui a gente
// precisa do Blob (pro visualizador com opção Caixa/Térmica, ver
// DialogVisualizarComprovante.tsx).
async function getBase64ImageFromUrl(url: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.onload = function () {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) return reject('Erro canvas');
      canvas.height = img.naturalHeight;
      canvas.width = img.naturalWidth;
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => reject('Erro ao carregar logo');
    img.src = url;
  });
}

function gerarLogoPadrao(): string {
  const canvas = document.createElement('canvas');
  canvas.width = 320;
  canvas.height = 90;
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';
  const gradient = ctx.createLinearGradient(0, 0, 320, 0);
  gradient.addColorStop(0, '#1f2937');
  gradient.addColorStop(1, '#374151');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.beginPath();
  ctx.arc(45, 45, 25, 0, 2 * Math.PI);
  ctx.fillStyle = '#10b981';
  ctx.fill();
  ctx.fillStyle = '#ffffff';
  ctx.font = 'bold 20px Arial';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.fillText('COMPROVANTE', 90, 45);
  return canvas.toDataURL('image/png');
}

const formatarMoeda = (valor: number) =>
  Number(valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Um canhoto — versão enxuta do comprovante, sem tabelas nem fundo colorido
// (só linhas finas), pra caber 4 iguais numa folha A4 e ainda sobrar
// contraste suficiente numa impressora comum/rápida. Os 4 saem idênticos
// (mesmo comprovante repetido) — o usuário corta em tiras e fica com uma
// via pra cada parte/arquivo, igual um canhoto de carnê de verdade.
function montarCanhoto(imageData: string, dados: IComprovanteBaixaDados): any {
  // Recibo de pagamento sai sozinho na folha — então pode ser maior (a
  // pedido); o comprovante de recebimento (4 vias) mantém o tamanho.
  const s = dados.tipo === 'pagamento' ? 1.45 : 1;
  const nomeEmpresa = dados.empresaFantasia || dados.empresaRazaoSocial || '';
  const textos = textosComprovante(dados);
  const referencia = dados.contas.map((c) => c.historico).filter(Boolean).join('; ') || '—';
  const titulos = dados.contas.map((c) => c.numero || '***').join(', ');
  const formaPagamentoTexto = dados.formasPagamento.length > 0
    ? dados.formasPagamento.map((f) => `${f.nome} ${formatarMoeda(f.valor)}`).join(' + ')
    : 'Recebimento';

  const miolo = {
    stack: [
      {
        columns: [
          { image: imageData, width: 30 * s, alignment: 'left' },
          {
            width: '*',
            stack: [
              ...(nomeEmpresa ? [{ text: nomeEmpresa, bold: true, fontSize: 9 * s }] : []),
              { text: dados.contaNome, fontSize: 6.5 * s, color: '#6b7280' },
            ],
            margin: [6, 1, 0, 0],
          },
          {
            width: 160,
            stack: [
              { text: textos.titulo, bold: true, fontSize: 8 * s, alignment: 'right' },
              { text: `Data: ${moment(dados.datalan).format('DD/MM/YYYY')}`, fontSize: 7 * s, alignment: 'right', color: '#374151' },
            ],
            margin: [0, 1, 0, 0],
          },
        ],
        columnGap: 6,
        margin: [0, 0, 0, 5],
      },
      { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 495, y2: 0, lineWidth: 0.75, lineColor: '#9ca3af' }], margin: [0, 0, 0, 6] },
      {
        columns: [
          {
            width: '*',
            stack: [
              { text: [{ text: `${textos.parceiro}: `, bold: true }, dados.parceiro], fontSize: 8 * s, margin: [0, 0, 0, 2] },
              { text: [{ text: 'Referência: ', bold: true }, referencia], fontSize: 8 * s, margin: [0, 0, 0, 2] },
              { text: [{ text: 'Título(s): ', bold: true }, titulos], fontSize: 8 * s, margin: [0, 0, 0, 2] },
              { text: [{ text: 'Forma de pgto: ', bold: true }, formaPagamentoTexto], fontSize: 8 * s },
              ...(dados.combinado?.trim()
                ? [{ text: [{ text: 'Combinado: ', bold: true }, dados.combinado.trim()], fontSize: 8 * s, margin: [0, 2, 0, 0] }]
                : []),
            ],
          },
          {
            width: 130,
            stack: [
              { text: textos.valor, fontSize: 7 * s, color: '#6b7280', alignment: 'right' },
              { text: formatarMoeda(dados.totalBaixado), fontSize: 15 * s, bold: true, alignment: 'right' },
            ],
          },
        ],
        columnGap: 8,
        margin: [0, 0, 0, 8],
      },
      ...(dados.tipo === 'pagamento'
        ? [{ text: declaracaoRecebimento(dados), fontSize: 8 * s, alignment: 'justify', margin: [0, 0, 0, 4] }]
        : []),
      {
        columns: [
          {
            width: '*',
            stack: [
              { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 210 + (s - 1) * 60, y2: 0, lineWidth: 0.5, lineColor: '#9ca3af' }], margin: [0, 8 + (s - 1) * 60, 0, 2] },
              { text: textos.assinaturaParceiro, fontSize: 6.5 * s, color: '#6b7280' },
              ...(dados.tipo === 'pagamento' ? [{ text: linhaAssinaturaRecebedor(dados), fontSize: 6.5 * s, bold: true }] : []),
            ],
          },
          {
            width: '*',
            alignment: 'right',
            stack: [
              { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 210 + (s - 1) * 60, y2: 0, lineWidth: 0.5, lineColor: '#9ca3af' }], margin: [0, 8 + (s - 1) * 60, 0, 2], alignment: 'right' },
              { text: 'Assinatura da Empresa', fontSize: 6.5 * s, color: '#6b7280', alignment: 'right' },
            ],
          },
        ],
      },
    ],
    margin: [10 * s, 8 * s, 10 * s, 8 * s],
  };

  // Moldura fina (só borda, sem preenchimento) em volta do canhoto — mesma
  // técnica do carnê de venda (impressaocarne.ts), sem cor de fundo.
  return {
    table: { widths: ['*'], body: [[miolo]] },
    layout: {
      hLineWidth: () => 1,
      vLineWidth: () => 1,
      hLineColor: () => '#9ca3af',
      vLineColor: () => '#9ca3af',
      paddingLeft: () => 0,
      paddingRight: () => 0,
      paddingTop: () => 0,
      paddingBottom: () => 0,
    },
  };
}

// Comprovante de uma baixa de Contas a Receber — gerado inteiramente com o
// que o DialogBaixa.tsx já tem em mãos no momento da confirmação (contas
// selecionadas + formas de pagamento usadas), sem chamada nova ao backend.
// Sai como 4 canhotos IGUAIS numa folha A4 só, separados por linha
// pontilhada de corte — igual o carnê de venda, só que aqui são 4 vias do
// mesmo comprovante, não parcelas diferentes.
// Recibo de pagamento (Contas a Pagar): o mesmo canhoto, mas UM só por
// recibo (não as 4 vias) — `demais` são os recibos das outras parcelas
// ("Todas as parcelas"), um canhoto por parcela, separados pela linha de corte.
export async function gerarComprovanteBaixa(dados: IComprovanteBaixaDados, demais: IComprovanteBaixaDados[] = []): Promise<Blob> {
  // Precisa ser setado aqui dentro, com esse caminho exato
  // (`pdfFonts.pdfMake.vfs`, não `pdfFonts.vfs`) — é como o pacote
  // `pdfmake` 0.2.7 realmente expõe o vfs (ver impressaovenda01.ts, que já
  // fazia certo). Setar errado deixa `pdfMake.vfs` undefined e quebra
  // qualquer `bold: true` com "File 'Roboto-Medium.ttf' not found".
  (pdfMake as any).fonts = {
    Roboto: {
      normal: 'Roboto-Regular.ttf',
      bold: 'Roboto-Medium.ttf',
      italics: 'Roboto-Italic.ttf',
      bolditalics: 'Roboto-MediumItalic.ttf',
    },
  };
  if ((pdfFonts as any)?.pdfMake?.vfs) {
    (pdfMake as any).vfs = (pdfFonts as any).pdfMake.vfs;
  }

  let imageData: string;
  if (dados.logo && dados.logo.trim() !== '') {
    const baseUrl = (window as any).env?.REACT_APP_URL_IMGLOG || '';
    const imageUrl = baseUrl + dados.logo;
    try {
      imageData = await getBase64ImageFromUrl(imageUrl);
    } catch {
      imageData = gerarLogoPadrao();
    }
  } else {
    imageData = gerarLogoPadrao();
  }

  const content: any[] = [];
  const canhotos = dados.tipo === 'pagamento' ? [dados, ...demais] : [dados, dados, dados, dados];
  for (let i = 0; i < canhotos.length; i++) {
    content.push({ ...montarCanhoto(imageData, canhotos[i]), unbreakable: true });
    if (i < canhotos.length - 1) {
      content.push({
        canvas: [{ type: 'line', x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 1, dash: { length: 3, space: 2 }, lineColor: '#9ca3af' }],
        margin: [0, 8, 0, 8],
      });
    }
  }

  const documentDefinition: any = {
    pageSize: 'A4',
    pageMargins: [30, 24, 30, 24],
    content,
  };

  return new Promise((resolve, reject) => {
    try {
      const pdfDocGenerator = pdfMake.createPdf(documentDefinition);
      pdfDocGenerator.getBlob((blob: Blob) => resolve(blob));
    } catch (error) {
      reject(error);
    }
  });
}
